/*#
# Notices: an ecosystem change, announced once

A change every repo should make — re-copy a template, raise a dependency
floor — reaches a repo only if its agent reads the right page. A **notice**
makes the board tell it (board #2548): every onboarded project's brief
shows its owed notices until each is adopted or declared not applicable.

- **A notice** is a `type:policy` entry tagged `targets:all` or
  `targets:<project>`: what to change and why, with a link to the
  instructions. It's the inverse of a practice, which a project opts into.
- **Checks are data, never commands.** A `check:` tag names one of a fixed
  set of kinds the CLI runs in the repo; nothing a notice says is ever
  executed, so the board can't run code in every repo:
  - `check:file-exists:<path>`
  - `check:file-equals:<path>@<sha256>`: the file's bytes, pinned
  - `check:file-contains:<path>|<text>`
  - `check:lock-resolves:<package>>=<version>`: in `bun.lock` or
    `package-lock.json`
- **Only where it applies.** `applies-if:<check>` (the same kinds, e.g.
  `applies-if:file-exists:.github/workflows/publish.yml`) is checked in
  the repo too: where it fails, the notice is recorded not applicable,
  with that reason.
- **Adoption is verified where it can be.** `virta notices` runs the checks
  in the repo it's in; when they all pass it records
  `adopted:<project>@<fingerprint>` on the notice. A changed check is a
  new fingerprint, so adoption reopens. A notice with no checks is
  adopted by saying so (`virta notices adopt #n`), and any notice can be
  declared `not-applicable:<project>` with the reason.
- **A project's own entry can override it** (board #2497's precedence):
  an open entry with `scope:<project>` and `overrides:#n` releases that
  project, and the matrix says by which entry — never silently.
- **It informs; the repo's agent acts.** The board edits no repo, and a
  notice never enrolls a project: only projects on the board see them.

The notice's own tags are the adoption matrix: who has, who hasn't.
*/

/*{ "parent": "Reference", "order": 41 }*/

import type { Task, VirtaEvent } from './model'
import type { VirtaStore } from './store'
import { tagValues, TYPE_KEY } from './tags'

export const TARGETS_KEY = 'targets'
export const CHECK_KEY = 'check'
export const ADOPTED_KEY = 'adopted'
export const NOT_APPLICABLE_KEY = 'not-applicable'
/** A condition, in the check kinds, for whether a notice applies to a repo at all */
export const APPLIES_IF_KEY = 'applies-if'

export type NoticeCheck =
  | { kind: 'file-exists'; path: string }
  | { kind: 'file-equals'; path: string; sha256: string }
  | { kind: 'file-contains'; path: string; text: string }
  | { kind: 'lock-resolves'; pkg: string; min: string }

/**
 * Why a check's path can't be used, or '': a check reads inside the repo
 * only. No absolute path, no `..` segment, no backslash, no empty or `.`
 * segment — refused at the tag, before anything is read (board #2835: a
 * check's result is written to the board, so a path outside the repo
 * would make every repo answer questions about its machine's files).
 */
export function pathRefusal(path: string): string {
  if (!path) return 'a check needs a path'
  if (path.startsWith('/') || /^[a-z]:/i.test(path) || path.includes('\\'))
    return `${path}: a check reads inside the repo only (no absolute path)`
  if (path.split('/').some((seg) => seg === '..' || seg === '' || seg === '.'))
    return `${path}: a check reads inside the repo only (no '..', '.' or empty segments)`
  return ''
}

/** A `check:` tag's value as a check, or why it isn't one. */
export function parseCheck(value: string): NoticeCheck | string {
  const c = parseCheckShape(value)
  if (typeof c === 'string' || c.kind === 'lock-resolves') return c
  const why = pathRefusal(c.path)
  return why ? `${value}: ${why}` : c
}

function parseCheckShape(value: string): NoticeCheck | string {
  const i = value.indexOf(':')
  const kind = i < 0 ? value : value.slice(0, i)
  const rest = i < 0 ? '' : value.slice(i + 1)
  switch (kind) {
    case 'file-exists':
      return rest ? { kind, path: rest } : `${value}: needs a path`
    case 'file-equals': {
      const m = /^(.+)@([0-9a-f]{64})$/i.exec(rest)
      return m
        ? { kind, path: m[1]!, sha256: m[2]!.toLowerCase() }
        : `${value}: needs <path>@<sha256>`
    }
    case 'file-contains': {
      const at = rest.indexOf('|')
      return at > 0 && at < rest.length - 1
        ? { kind, path: rest.slice(0, at), text: rest.slice(at + 1) }
        : `${value}: needs <path>|<text>`
    }
    case 'lock-resolves': {
      const m = /^(@?[^@>=\s]+)>=(\d+(?:\.\d+){0,2})$/.exec(rest)
      return m
        ? { kind, pkg: m[1]!, min: m[2]! }
        : `${value}: needs <package>>=<version>`
    }
  }
  return `${value}: not a check kind (file-exists, file-equals, file-contains, lock-resolves)`
}

/** Whether a task is a notice: an open policy with a target. */
export const isNotice = (t: Task) =>
  !t.closedAt &&
  tagValues(t.tags, TYPE_KEY)[0] === 'policy' &&
  tagValues(t.tags, TARGETS_KEY).length > 0

/** Whether a notice targets a project. */
export const targets = (t: Task, project: string) =>
  tagValues(t.tags, TARGETS_KEY).some((v) => v === 'all' || v === project)

/** A notice's checks: the parsed ones, and the tags that aren't checks. */
export function checksOf(t: Task): {
  checks: NoticeCheck[]
  invalid: string[]
} {
  const checks: NoticeCheck[] = []
  const invalid: string[] = []
  for (const v of tagValues(t.tags, CHECK_KEY)) {
    const c = parseCheck(v)
    if (typeof c === 'string') invalid.push(c)
    else checks.push(c)
  }
  return { checks, invalid }
}

/**
 * A notice's check fingerprint: what an adoption was verified against.
 * Changing a check changes it, so adoption reopens. FNV-1a over the sorted
 * check tags; '' when it has none (adoption is then the agent's word).
 */
export function fingerprint(t: Task): string {
  const tags = tagValues(t.tags, CHECK_KEY).sort().join('\n')
  if (!tags) return ''
  let h = 0x811c9dc5
  for (let i = 0; i < tags.length; i++) {
    h ^= tags.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}

/** The tag that records a project's adoption of a notice, as it stands now. */
export const adoptionTag = (t: Task, project: string) => {
  const fp = fingerprint(t)
  return `${ADOPTED_KEY}:${project}${fp ? `@${fp}` : ''}`
}

export type NoticeState =
  | 'owed'
  | 'adopted'
  | 'not-applicable'
  | 'overridden'
  /** a security notice the project's own entry overrides, until the owner approves */
  | 'override-pending'

/** What the notice rules read: the entries, and an entry's events (for who approved). */
export interface NoticeBoard {
  tasks: readonly Task[]
  events(taskId: string): VirtaEvent[]
}

/** Whether a notice is a security fix: what nobody opts out of alone (board #2840). */
export const isSecurity = (t: Pick<Task, 'tags'>) =>
  t.tags.includes('security') ||
  tagValues(t.tags, 'topic').includes('security') ||
  tagValues(t.tags, 'kind').includes('security')

/** The tag the owner adds to a project's entry to let it override a security notice. */
export const overrideApprovalTag = (notice: Task) =>
  `override-approved:#${notice.num}`

/**
 * The project's own entry that overrides a notice, if any (board #2497's
 * precedence: a project-scoped entry beats a global default): an open
 * Library entry with `scope:<project>` and `overrides:#n`.
 */
export function overriderOf(
  board: NoticeBoard,
  notice: Task,
  project: string
): Task | null {
  return (
    board.tasks.find(
      (t) =>
        !t.closedAt &&
        t.id !== notice.id &&
        tagValues(t.tags, 'scope').includes(project) &&
        tagValues(t.tags, 'overrides').includes(`#${notice.num}`)
    ) ?? null
  )
}

/**
 * Whether the owner approved an entry's override of a security notice
 * (board #2840): the latest event adding `override-approved:#n` was made
 * by a person signed in on the board — stamped with no token — never by an
 * agent's token, which could otherwise opt its own repo out of a security
 * fix. A local log has no stamps: there the tag is enough.
 */
export function overrideApproved(
  board: NoticeBoard,
  entry: Task,
  notice: Task
): boolean {
  const tag = overrideApprovalTag(notice)
  if (!entry.tags.includes(tag)) return false
  const added = board
    .events(entry.id)
    .filter((e) => e.kind === 'tagged' && e.payload.tag === tag)
    .pop()
  if (!added) return false
  return !added.attribution || !added.attribution.token
}

/** Where a project stands on a notice. */
export function noticeState(
  t: Task,
  project: string,
  board: NoticeBoard = { tasks: [], events: () => [] }
): NoticeState {
  if (t.tags.includes(`${NOT_APPLICABLE_KEY}:${project}`))
    return 'not-applicable'
  if (t.tags.includes(adoptionTag(t, project))) return 'adopted'
  const over = overriderOf(board, t, project)
  if (!over) return 'owed'
  return !isSecurity(t) || overrideApproved(board, over, t)
    ? 'overridden'
    : 'override-pending'
}

const PRIORITY = ['urgent', 'high', 'medium', 'low']
/** Most important first: security, then priority, then oldest. */
const severity = (t: Task) =>
  (isSecurity(t) ? 0 : 10) +
  ((i) => (i < 0 ? PRIORITY.length : i))(
    PRIORITY.indexOf(tagValues(t.tags, 'priority')[0] ?? '')
  )

export interface NoticeLine {
  task: Task
  state: NoticeState
}

/** The notices a project sees, most important first, with where it stands on each. */
export function noticesFor(store: VirtaStore, project: string): NoticeLine[] {
  return store.tasks
    .filter((t) => isNotice(t) && targets(t, project))
    .sort((a, b) => severity(a) - severity(b) || a.num - b.num)
    .map((task) => ({ task, state: noticeState(task, project, store) }))
}

/** What a check needs from the repo: a file's text and bytes, or null. */
export interface RepoFiles {
  text(path: string): string | null
  sha256(path: string): Promise<string | null>
}

/** A semver-ish `a >= b` over dotted numbers (pre-release tails ignored). */
export function versionAtLeast(have: string, min: string): boolean {
  const parse = (v: string) =>
    v
      .replace(/^[^\d]*/, '')
      .split(/[.+-]/)
      .slice(0, 3)
      .map((x) => Number(x) || 0)
  const a = parse(have)
  const b = parse(min)
  for (let i = 0; i < 3; i++) {
    if ((a[i] ?? 0) > (b[i] ?? 0)) return true
    if ((a[i] ?? 0) < (b[i] ?? 0)) return false
  }
  return true
}

/** Every version of a package a lockfile resolves. */
export function lockedVersions(lock: string, pkg: string): string[] {
  const out: string[] = []
  const name = pkg.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&')
  // bun.lock: "<pkg>@<version>" in each resolution
  for (const m of lock.matchAll(new RegExp(`"${name}@(\\d[^"\\s,]*)"`, 'g')))
    out.push(m[1]!)
  // package-lock.json: "node_modules/<pkg>": { "version": "x" }
  for (const m of lock.matchAll(
    new RegExp(
      `"(?:[^"]*/)?node_modules/${name}"\\s*:\\s*\\{[^}]*?"version"\\s*:\\s*"([^"]+)"`,
      'g'
    )
  ))
    out.push(m[1]!)
  return out
}

/**
 * Run one check against the repo: whether it passes, why not, and what it
 * saw — recorded with an adoption (board #2839), so a claim rests on
 * observed values anyone can inspect, not on a flag.
 */
export async function runCheck(
  check: NoticeCheck,
  repo: RepoFiles
): Promise<{ ok: boolean; why: string; observed: string }> {
  switch (check.kind) {
    case 'file-exists':
      return repo.text(check.path) !== null
        ? { ok: true, why: '', observed: `${check.path} exists` }
        : { ok: false, why: `${check.path} is missing`, observed: '' }
    case 'file-equals': {
      const sha = await repo.sha256(check.path)
      if (sha === null)
        return { ok: false, why: `${check.path} is missing`, observed: '' }
      const observed = `${check.path} sha256 ${sha}`
      return sha === check.sha256
        ? { ok: true, why: '', observed }
        : {
            ok: false,
            why: `${check.path} differs from the pinned version`,
            observed,
          }
    }
    case 'file-contains': {
      const text = repo.text(check.path)
      if (text === null)
        return { ok: false, why: `${check.path} is missing`, observed: '' }
      return text.includes(check.text)
        ? {
            ok: true,
            why: '',
            observed: `${check.path} contains "${check.text}"`,
          }
        : {
            ok: false,
            why: `${check.path} doesn't contain "${check.text}"`,
            observed: '',
          }
    }
    case 'lock-resolves': {
      const lock = repo.text('bun.lock') ?? repo.text('package-lock.json')
      if (lock === null)
        return {
          ok: false,
          why: 'no bun.lock or package-lock.json',
          observed: '',
        }
      const versions = lockedVersions(lock, check.pkg)
      const observed = versions.length
        ? `${check.pkg} resolves to ${[...new Set(versions)].join(', ')}`
        : `${check.pkg} isn't in the lockfile`
      // not in the tree at all: nothing below the floor
      if (!versions.length) return { ok: true, why: '', observed }
      const low = versions.filter((v) => !versionAtLeast(v, check.min))
      return low.length
        ? {
            ok: false,
            why: `${check.pkg} resolves to ${low.join(', ')} (needs >=${
              check.min
            })`,
            observed,
          }
        : { ok: true, why: '', observed }
    }
  }
}

/** A notice by #n or id, or why not. */
function noticeOrWhy(store: VirtaStore, ref: string): Task | string {
  const m = /^#?(\d+)$/.exec(ref.trim())
  const t = m ? store.byNum(Number(m[1])) : store.get(ref)
  if (!t) return `no task ${ref}`
  if (!isNotice(t))
    return `#${t.num} isn't a notice (an open type:policy with a targets: tag)`
  return t
}

/**
 * Record a project's adoption of a notice that has no checks: on the
 * agent's word (board #2548). A notice with checks can't be adopted this
 * way — not by the CLI, not over MCP (board #2839): only
 * `recordVerifiedAdoption`, which `virta project notices` calls after
 * running the checks in the repo.
 */
export async function adoptNotice(
  store: VirtaStore,
  ref: string,
  project: string
): Promise<Task> {
  const t = noticeOrWhy(store, ref)
  if (typeof t === 'string') throw new Error(t)
  if (!targets(t, project))
    throw new Error(`#${t.num} doesn't target ${project}`)
  if (checksOf(t).checks.length)
    throw new Error(
      `#${t.num} has checks: run \`virta project notices\` in ${project}'s repo, which runs them and records the adoption with what it saw`
    )
  const tag = adoptionTag(t, project)
  if (!t.tags.includes(tag)) await store.tag(t.id, tag)
  return store.get(t.id)!
}

/**
 * Record an adoption the checks verified, with what they observed (board
 * #2839): the adoption tag and a comment naming the values seen — the
 * hash, the versions — in one commit. Not a verb: only the CLI, having run
 * the checks in the repo, calls it, so the claim always carries evidence.
 */
export async function recordVerifiedAdoption(
  store: VirtaStore,
  ref: string,
  project: string,
  observed: string[]
): Promise<Task> {
  const t = noticeOrWhy(store, ref)
  if (typeof t === 'string') throw new Error(t)
  if (!observed.length) throw new Error('a verified adoption names what it saw')
  const tag = adoptionTag(t, project)
  if (t.tags.includes(tag)) return t
  await store.transaction(async (scratch) => {
    await scratch.tag(t.id, tag)
    await scratch.comment(
      t.id,
      `Adopted by ${project}, checks passed in its repo:\n${observed
        .map((o) => `- ${o}`)
        .join('\n')}`
    )
  })
  return store.get(t.id)!
}

/** Declare a notice not applicable to a project, with the reason (rollouts.md's "Not applicable: … — why"). */
export async function declineNotice(
  store: VirtaStore,
  ref: string,
  project: string,
  reason: string
): Promise<Task> {
  const t = noticeOrWhy(store, ref)
  if (typeof t === 'string') throw new Error(t)
  if (!reason.trim()) throw new Error('not applicable needs the reason')
  await store.transaction(async (scratch) => {
    await scratch.tag(t.id, `${NOT_APPLICABLE_KEY}:${project}`)
    await scratch.comment(
      t.id,
      `Not applicable to ${project}: ${reason.trim()}`
    )
  })
  return store.get(t.id)!
}

export interface NoticeSummary {
  num: number
  title: string
  /** the conditions it applies under (`applies-if:` tags) */
  appliesIf: string[]
  state?: NoticeState
  checks: string[]
  adopted: string[]
  notApplicable: string[]
  /** projects whose own scoped entry overrides it (board #2497): `name (#m)` */
  overridden: string[]
  /** a security notice's overrides the owner hasn't approved yet (board #2840): still owed */
  overridePending: string[]
  /** projects on the board it targets that have done neither */
  owed: string[]
}

/** The notices, with their adoption across the board's enrolled projects (or one project's view). */
export function noticeSummaries(
  store: VirtaStore,
  project?: string
): NoticeSummary[] {
  const enrolled = store.projects.filter((p) => p.enrolled).map((p) => p.name)
  const list = project
    ? noticesFor(store, project).map((l) => l.task)
    : store.tasks
        .filter(isNotice)
        .sort((a, b) => severity(a) - severity(b) || a.num - b.num)
  return list.map((t) => {
    const reach = enrolled.filter((p) => targets(t, p))
    return {
      num: t.num,
      title: t.title,
      ...(project ? { state: noticeState(t, project, store) } : {}),
      checks: tagValues(t.tags, CHECK_KEY),
      appliesIf: tagValues(t.tags, APPLIES_IF_KEY),
      adopted: reach.filter((p) => noticeState(t, p, store) === 'adopted'),
      notApplicable: reach.filter(
        (p) => noticeState(t, p, store) === 'not-applicable'
      ),
      overridden: reach
        .filter((p) => noticeState(t, p, store) === 'overridden')
        .map((p) => `${p} (#${overriderOf(store, t, p)!.num})`),
      overridePending: reach
        .filter((p) => noticeState(t, p, store) === 'override-pending')
        .map((p) => `${p} (#${overriderOf(store, t, p)!.num})`),
      owed: reach.filter((p) =>
        ['owed', 'override-pending'].includes(noticeState(t, p, store))
      ),
    }
  })
}

export interface SettledNotice {
  num: number
  title: string
  /** where it stands now, after this run */
  state: NoticeState
  /** this run recorded it (adopted, or not applicable) */
  recorded: boolean
  /** one line for a person or an agent: what it is, and what to do */
  line: string
}

/**
 * Run each owed notice's checks in the repo and record what they show
 * (board #2845): adopted with what the checks saw, or not applicable when
 * an `applies-if` condition fails. Data-only checks, confined to the repo
 * (#2835); it records facts and edits nothing in the repo. A notice with
 * no checks stays owed: only the agent can say it's done.
 */
export async function settleNotices(
  store: VirtaStore,
  project: string,
  files: RepoFiles
): Promise<SettledNotice[]> {
  const out: SettledNotice[] = []
  for (const { task: t, state } of noticesFor(store, project)) {
    const base = { num: t.num, title: t.title }
    if (state === 'override-pending') {
      out.push({
        ...base,
        state,
        recorded: false,
        line: `#${t.num} ${t.title}: owed — this repo's override of a security notice waits for the owner's approval on the board`,
      })
      continue
    }
    if (state !== 'owed') {
      out.push({
        ...base,
        state,
        recorded: false,
        line: `#${t.num} ${t.title} (${state})`,
      })
      continue
    }
    const unmet: string[] = []
    for (const tag of tagValues(t.tags, APPLIES_IF_KEY)) {
      const check = parseCheck(tag)
      if (typeof check === 'string') continue
      const r = await runCheck(check, files)
      if (!r.ok) unmet.push(r.why)
    }
    if (unmet.length) {
      await declineNotice(
        store,
        t.id,
        project,
        `its condition fails here: ${unmet.join('; ')}`
      )
      out.push({
        ...base,
        state: 'not-applicable',
        recorded: true,
        line: `#${t.num} ${t.title}: not applicable here (${unmet.join(
          '; '
        )}); recorded`,
      })
      continue
    }
    const { checks, invalid } = checksOf(t)
    if (!checks.length && !invalid.length) {
      out.push({
        ...base,
        state,
        recorded: false,
        line: `#${t.num} ${t.title}: owed. Read it (virta show #${t.num}); when done, \`virta project adopt #${t.num}\`, or \`virta project na #${t.num} "why"\``,
      })
      continue
    }
    const failing = [...invalid]
    const observed: string[] = []
    for (const check of checks) {
      const r = await runCheck(check, files)
      if (!r.ok) failing.push(r.why)
      else observed.push(r.observed)
    }
    if (failing.length) {
      out.push({
        ...base,
        state,
        recorded: false,
        line: `#${t.num} ${t.title}: owed — ${failing.join(
          '; '
        )} (virta show #${t.num})`,
      })
      continue
    }
    await recordVerifiedAdoption(store, t.id, project, observed)
    out.push({
      ...base,
      state: 'adopted',
      recorded: true,
      line: `#${t.num} ${
        t.title
      }: adopted (its checks pass here: ${observed.join('; ')})`,
    })
  }
  return out
}
